/**
 * Стани помилок (UX-50): «Немає доступу», «Не вдалося відкрити» і «Триває оновлення». Один вибір за
 * видом збою (`failureKind`), тож екран не вирішує сам, що показати на 403, 404 чи 503: він передає
 * помилку й дію «Спробувати ще раз». `ErrorState.tsx` бере список, тож він і його тексти
 * (`support-pages.ts`) — у першому екрані. «Сторінку не знайдено» — `NotFoundView` у лінивому
 * `app/routes/route-errors.tsx`. Сторінка помилки не має залежати від того, що не завантажилось:
 * якщо чанк `route-errors.tsx` не доїхав, `root.tsx` показує запасний `RootError.tsx` з головного чанка.
 * Текст — що сталося → що зробити → код для підтримки; жодних повідомлень сервера й стеків.
 */
import { Link } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef } from 'react';
import { common as c } from '../i18n/uk/common.ts';
import { supportCore as s } from '../i18n/uk/support-core.ts';
import { supportPages as t } from '../i18n/uk/support-pages.ts';
import { StateView } from '../ui/StateView.tsx';
import { supportCode, traceIdOf } from './classify.ts';
import { expectedWaitSeconds, failureKind } from './failure-kind.ts';

/** Як часто сторінка «Триває оновлення» перевіряє готовність (reliability-releases.md, розд. 12.4). */
export const MAINTENANCE_CHECK_MS = 30_000;

interface PageProps {
  /** Сторінка замість області: `h1` і фокус на заголовку. */
  page?: boolean;
}

/**
 * Немає прав. Що саме закрито і хто веде запис, текст не розкриває (states.md,
 * розд. 4: рішення Security): лише «не вистачає прав» і куди звернутись.
 */
export function NoAccessState({ error, page = true }: PageProps & { error?: unknown }) {
  return (
    <StateView
      icon="lock"
      title={t.noAccessTitle}
      text={t.noAccessText}
      page={page}
      reportLabel={s.requestAccess}
      {...(error === undefined ? {} : { code: supportCode(error), traceId: traceIdOf(error) })}
    >
      <Link className="btn btn--primary" to="/">
        {s.toHome}
      </Link>
    </StateView>
  );
}

/**
 * «Триває оновлення» (503): очікуваний час із `Retry-After`, якщо він є, і
 * автоматична перевірка кожні 30 с. `onCheck` перезапускає те, що впало; коли воно вдасться,
 * ця сторінка зникне сама. Живий регіон один і статичний: секунди не оголошуються.
 */
export function MaintenanceState({
  error,
  onCheck,
  everyMs = MAINTENANCE_CHECK_MS,
  page = true,
}: PageProps & {
  error?: unknown;
  onCheck?: (() => void) | undefined;
  /** Період перевірки; у тестах коротший. */
  everyMs?: number;
}) {
  const check = useRef(onCheck);
  check.current = onCheck;
  useEffect(() => {
    if (!check.current) return;
    const timer = setInterval(() => {
      // Фонова вкладка не б'є по оновлюваному серверу: перевірить, коли людина повернеться.
      if (!document.hidden) check.current?.();
    }, everyMs);
    return () => clearInterval(timer);
  }, [everyMs]);
  const wait = expectedWaitSeconds(error);
  return (
    <StateView
      icon="clock"
      title={t.maintenanceTitle}
      text={[t.maintenanceText, wait ? t.maintenanceEta(wait) : null, t.maintenanceChecking]
        .filter(Boolean)
        .join(' ')}
      page={page}
    >
      {onCheck && (
        <button className="btn btn--primary" type="button" onClick={onCheck}>
          {c.retry}
        </button>
      )}
    </StateView>
  );
}

interface ErrorStateProps extends PageProps {
  error: unknown;
  /** Перезапустити те, що впало; без нього кнопки «Спробувати ще раз» немає. */
  onRetry?: (() => void) | undefined;
  /** Свій заголовок і текст для звичайного збою (картка: «Не вдалося відкрити запис»). */
  title?: string | undefined;
  text?: string | undefined;
  /** Ще одна дія поруч із повтором (наприклад, «До списку»). */
  children?: ReactNode;
}

/**
 * Заголовок вкладки для збою (UX-07): його ставить той, хто показує стан, — стан сам вкладку не
 * чіпає, бо екран (картка, список) має власний заголовок і порядок ефектів вирішував би, чий виграє.
 */
export function errorPageTitle(error: unknown): string {
  const kind = failureKind(error);
  if (kind === 'forbidden') return t.noAccessTitle;
  if (kind === 'maintenance') return t.maintenanceTitle;
  return c.errorTitle;
}

/** Збій екрана: вид сторінки — за видом збою, код і `traceId` — із самої помилки. */
export function ErrorState({
  error,
  onRetry,
  title,
  text,
  page = true,
  children,
}: ErrorStateProps) {
  const kind = failureKind(error);
  if (kind === 'forbidden') return <NoAccessState error={error} page={page} />;
  if (kind === 'maintenance')
    return <MaintenanceState error={error} onCheck={onRetry} page={page} />;
  const offline = kind === 'offline';
  return (
    <ServerErrorView
      error={error}
      page={page}
      title={title ?? (offline ? t.offlineTitle : t.serverTitle)}
      text={text ?? (offline ? t.offlineText : t.serverText)}
      onRetry={onRetry}
    >
      {children}
    </ServerErrorView>
  );
}

function ServerErrorView({
  error,
  page,
  title,
  text,
  onRetry,
  children,
}: {
  error: unknown;
  page: boolean;
  title: string;
  text: string;
  onRetry: (() => void) | undefined;
  children?: ReactNode;
}) {
  return (
    <StateView
      icon="alert-circle"
      tone="error"
      title={title}
      text={text}
      code={supportCode(error)}
      traceId={traceIdOf(error)}
      page={page}
    >
      {onRetry && (
        <button className="btn btn--primary" type="button" onClick={onRetry}>
          {c.retry}
        </button>
      )}
      {children}
    </StateView>
  );
}
